import {
  fieldAddProductVulcan,
  fieldNewOrExistingCustomer,
  fieldProductVulcan
} from '../../../utils/constant';

/**
 * Pure rewrite of `filterProduct` (vulcanHelpers/customerSection/filterProduct.js)
 * as a `fieldRules` rule for the "Add Product" multi-select, in the WLN (non-Vulcan)
 * flow: mutually-exclusive Fibre/Copper, Internet-Backup gating (SOF-3388: Existing
 * customer needs HSIA; SOF-3398: New/unset customer needs Copper or Fibre Internet,
 * and in that case Backup pairs with Copper instead of being hidden by it).
 *
 * The original returned `{ filteredOptions, update }` AND mutated
 * `formFields[addProduct].value` in place. Here both become one self-patch on the
 * field: `{ options, value }`. It reads the EVOLVING `state.options` (a prior rule
 * — CustomerHelpers' product filter — runs first), matching the old sequential
 * `formFields.options` mutation. Verified by rules/filterProduct.diff.test.js.
 *
 * @param {{orderData: Object, t: Object}} ctx
 * @returns {import('../vulcanFieldRules').VulcanRule}
 */
export function makeFilterProductRule(ctx) {
  const { orderData, t } = ctx;
  const {
    vulFibreInternet,
    vulCopperInternet,
    vulInternetBackup,
    vulBusinessWifi,
    vulTSB,
    vulBusinessOptikTV,
    vulOfficePhone,
    vulExistingCustomer,
    vulHSIA
  } = t;
  const showProduct = [vulBusinessWifi, vulFibreInternet, vulInternetBackup];
  const productsThatHideBackup = [
    vulTSB,
    vulCopperInternet,
    vulBusinessOptikTV,
    vulOfficePhone
  ];

  return (field, state, values) => {
    if (field.name !== fieldAddProductVulcan) return undefined;
    const isVulcan = orderData.isThisVulcanOrder;
    const baseOptions = state.options || field.options || [];
    let options = baseOptions;
    let value = values[fieldAddProductVulcan];
    let update = false;
    let valueChanged = false;

    const isExisting =
      values[fieldNewOrExistingCustomer] === vulExistingCustomer;
    const productVulcan = values[fieldProductVulcan];
    const isHSIA =
      Array.isArray(productVulcan) && productVulcan.includes(vulHSIA);
    const addProductValue = values[fieldAddProductVulcan] || [];
    // New/unset customers: Internet Backup is offered alongside an internet
    // product (SOF-3398, fibre-east offer filtering) — pairs with Copper too,
    // not just Fibre/Wifi like the Existing-customer+HSIA case below.
    const hasCopperOrFibreInternet =
      addProductValue.includes(vulCopperInternet) ||
      addProductValue.includes(vulFibreInternet);
    const shouldShowInternetBackup = isExisting
      ? isHSIA
      : hasCopperOrFibreInternet;
    const backupCompatibleProducts = isExisting
      ? showProduct
      : [...showProduct, vulCopperInternet];
    const productsThatHideBackupForCustomer = isExisting
      ? productsThatHideBackup
      : productsThatHideBackup.filter(p => p !== vulCopperInternet);

    if (value && !isVulcan) {
      let filtered = options;
      if (value.includes(vulInternetBackup)) {
        value = value.filter(v => backupCompatibleProducts.includes(v));
        valueChanged = true;
        filtered = filtered.filter(o =>
          backupCompatibleProducts.includes(o.fields.value)
        );
        update = true;
      }
      if (value.includes(vulCopperInternet)) {
        filtered = filtered.filter(o => o.fields.value !== vulFibreInternet);
        update = true;
      }
      if (value.includes(vulFibreInternet)) {
        filtered = filtered.filter(o => o.fields.value !== vulCopperInternet);
        update = true;
      }
      if (productsThatHideBackupForCustomer.some(p => value.includes(p))) {
        value = value.filter(v => v !== vulInternetBackup);
        valueChanged = true;
        filtered = filtered.filter(o => o.fields.value !== vulInternetBackup);
        update = true;
      }
      options = filtered;
    }

    // Hide Internet Backup whenever it should not be shown for the current
    // customer type. Existing: only with HSIA. New/unset: only with Copper
    // or Fibre Internet selected (SOF-3398).
    if (!isVulcan && !shouldShowInternetBackup) {
      const optionsBase = update ? options : baseOptions;
      options = optionsBase.filter(o => o.fields.value !== vulInternetBackup);
      if (Array.isArray(value) && value.includes(vulInternetBackup)) {
        value = value.filter(v => v !== vulInternetBackup);
        valueChanged = true;
      }
      update = true;
    }

    if (!update && !valueChanged) return undefined;
    const patch = {};
    if (update) patch.options = options;
    if (valueChanged) patch.value = value;
    return patch;
  };
}

export default makeFilterProductRule;
